// useWebSocket.js
import { useEffect, useRef, useState } from 'react';

const useWebSocket = (url, reconnectInterval = 5000) => {
  const ws = useRef(null);
  const pingInterval = useRef(null);
  const [lastMessage, setLastMessage] = useState(null);

  // Use your WS base URL from env variables
  const WS_URL =
    process.env.REACT_APP_ENVIRONMENT === 'production'
      ? process.env.REACT_APP_PRODUCTION_WS_URL
      : process.env.REACT_APP_DEVELOPMENT_WS_URL;

  const connect = () => {
    let wsUrl = url;
    // If URL is relative, prepend WS_URL
    if (wsUrl.startsWith('/')) {
      wsUrl = WS_URL + wsUrl;
    } else if (wsUrl.includes('://')) {
      wsUrl = wsUrl.split('://')[1];
    }
    // Use the proper protocol based on page protocol
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    wsUrl = `${protocol}://${wsUrl}`;

    console.log("Attempting to connect to:", wsUrl);

    try {
      ws.current = new WebSocket(wsUrl);
    } catch (e) {
      console.error('Failed to create WebSocket:', e);
      return;
    }

    ws.current.onopen = () => {
      console.log('WebSocket connected to', wsUrl);
      // Clear any existing ping interval and start a new one
      if (pingInterval.current) clearInterval(pingInterval.current);
      pingInterval.current = setInterval(() => {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
          ws.current.send('ping');
        }
      }, 30000);
    };

    ws.current.onmessage = (event) => {
      // If the message is a simple string (like "pong"), handle it separately.
      if (event.data === "pong") {
        console.log("Received pong from server");
        return;
      }
      
      try {
        const parsed = JSON.parse(event.data);
        console.log('Message received (parsed):', parsed);
        setLastMessage(parsed);
      } catch (error) {
        console.error('Error parsing message, treating as string:', error);
        console.log('Message received (string):', event.data);
        setLastMessage(event.data);
      }
    };    

    ws.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.current.onclose = () => {
      console.log('WebSocket disconnected from', wsUrl);
      // Clear ping timer when connection closes
      if (pingInterval.current) clearInterval(pingInterval.current);
      // Try to reconnect after the specified interval
      setTimeout(connect, reconnectInterval);
    };
  };

  useEffect(() => {
    connect();
    return () => {
      if (ws.current) {
        ws.current.close();
      }
      if (pingInterval.current) {
        clearInterval(pingInterval.current);
      }
    };
  }, [url, WS_URL, reconnectInterval]);

  return { lastMessage };
};

export { useWebSocket };
